import React, { useCallback, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import styled from "styled-components/macro";

import { _api_upload_document } from "../../api/manage_document";
import { FILE_SIZE_LIMIT } from "../../features/documents/constants";

import { Flex } from "@fluentui/react-northstar";
import { _api_delete_document, _upload_file } from "../../api/tasks";
import Illustration from "../shared/Illustration";
import { BASE_URL_API } from "../../constants";

/**
 * @typedef {Object} DropzoneGlobalProps
 * @property {import("../../features/hr_documents/hooks/useLogicDialogAddDocument").DropzoneState} dz - Function to update the list of files.
 * @property {number} [maxFiles=5] - Maximum number of files allowed.
 * @property {number} [size_limit=FILE_SIZE_LIMIT] - Maximum file size allowed.
 * @property {Function} [onErrors=() => null] - Callback function to handle errors.
 */

/**
 * DropzoneGlobal component for handling file uploads with drag-and-drop functionality.
 *
 * @param {DropzoneGlobalProps} props - The component props.
 *
 * @returns {JSX.Element} The rendered DropzoneGlobal component.
 */
const DropzoneGlobal = ({
    dz,
    maxFiles = 5,
    size_limit = FILE_SIZE_LIMIT,
    onErrors = () => null,
}) => {
    const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
        // Initialiser la progression pour les nouveaux fichiers valides
        const initialProgress = {};

        acceptedFiles.forEach((file) => {
            initialProgress[file.name] = 0;
        });

        dz.setProgress((prevProgress) => ({
            ...prevProgress,
            ...initialProgress,
        }));

        dz.setFiles((prevFiles) => {
            return [
                ...prevFiles,
                ...acceptedFiles.map((pf) => {
                    return Object.defineProperty(pf, "sent", {
                        value: false,
                        writable: true,
                    });
                }),
            ];
        });
    }, []);

    React.useEffect(() => {
        if (dz.files?.length > 0) {
            uploadFiles();
        }
    }, [dz.files?.length]);

    const uploadFiles = () => {
        const xhr = new XMLHttpRequest();
        xhr.withCredentials = true;
        const formData = new FormData();
        const format_files = dz.files.filter((file) => !file.sent);

        for (let i = 0; i < format_files?.length; i++) {
            const current_file = format_files[i];
            if (!current_file?.is_default) {
                formData.append("uploaded_files", format_files[i]);
            }
        }

        // ne pas envoyer un FormData Empty.
        let isEmpty = true;
        for (const pair of formData.entries()) {
            isEmpty = false;
            break;
        }
        if (isEmpty) {
            // ne pas envoyer un FormData Empty.
            return;
        }

        xhr.open("POST", `${BASE_URL_API}/upload_file/`, true);

        // Track upload progress
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                let uploadedSize = event.loaded;

                const updatedProgress = {};
                format_files.forEach((file) => {
                    const fileSize = file.size;
                    const fileProgress = Math.min(1, uploadedSize / fileSize);
                    updatedProgress[file.name] = fileProgress * 100; // Percentage
                    uploadedSize -= fileSize;
                });

                dz.setProgress((prev) => ({ ...prev, ...updatedProgress }));
            }
        };

        xhr.onload = () => {
            if (xhr.status === 200) {
                const response = JSON.parse(xhr.response);
                if (response?.success) {
                    dz.setFiles((prevFiles) => {
                        return prevFiles.map((pf) => {
                            const isContained = response.files.find(
                                (file) => file.name === pf.name
                            );
                            return Object.defineProperty(pf, "sent", {
                                value: isContained !== -1 ? true : false,
                                writable: true,
                            });
                        });
                    });

                    dz.setFilesUploaded((old_files) => [
                        ...old_files,
                        ...response?.files,
                    ]);
                }
            } else {
                console.error("Upload failed:", xhr.statusText);
            }
        };

        xhr.onerror = () => {
            console.error("An error occurred during the upload.");
        };

        xhr.send(formData);
    };

    let disabled = dz.files?.length >= maxFiles;

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
        fileRejections,
    } = useDropzone({
        disabled: disabled,
        accept: {
            // Google Docs
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                [".docx"],
            "application/msword": [".doc"],
            // Google Sheets
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                [".xlsx"],
            "application/vnd.ms-excel": [".xls"],
            // Google Slides
            "application/vnd.openxmlformats-officedocument.presentationml.presentation":
                [".pptx"],
            "application/vnd.ms-powerpoint": [".ppt"],
            // PDF
            "application/pdf": [".pdf"],
            // IMAGES
            "image/jpeg": [".jpeg", ".jpg"],
            "image/png": [".png"],
            "image/gif": [".gif"],
            "image/bmp": [".bmp"],
            "image/tiff": [".tif", ".tiff"],
            "image/heif": [".heif", ".heic"],
            "image/webp": [".webp"],
        },
        maxFiles,
        maxSize: size_limit,
        multiple: true,
        onDrop: onDrop,
        onDropRejected: (err) => {
            const size_exceeded = [];
            const format_not_supported = [];
            let to_many_files = false;
            err.forEach((fileRejection) => {
                fileRejection.errors.forEach((error) => {
                    if (error.code === "file-invalid-type") {
                        format_not_supported.push(fileRejection.file.name);
                    }
                    if (error.code === "too-many-files") {
                        to_many_files = true;
                    }
                    if (error.code === "file-too-large") {
                        size_exceeded.push(fileRejection.file.name);
                    }
                });
            });
            let contentAlert = (
                <Flex column>
                    {to_many_files && (
                        <span>
                            Le nombre de fichiers dépasse la limite autorisée
                        </span>
                    )}
                    {format_not_supported.length > 0 && (
                        <span>
                            Un format n'est pas autorisé, "
                            {format_not_supported.join(" , ")}"
                        </span>
                    )}
                    {size_exceeded.length > 0 && (
                        <span>
                            Le poids est supérieur au poids maximal, "
                            {size_exceeded.join(" , ")}"
                        </span>
                    )}
                </Flex>
            );

            onErrors(contentAlert);
        },
    });

    const style = useMemo(
        () => ({
            ...baseStyle,
            opacity: disabled ? 0.3 : 1,
            cursor: disabled ? "not-allowed" : "pointer",
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isFocused, isDragAccept, isDragReject]
    );

    return (
        <div className="container" style={{ width: "100%" }}>
            <div {...getRootProps({ style })}>
                <input {...getInputProps()} />
                <Illustration
                    filename={"shared/dropzone_upload_file.svg"}
                    width="120"
                />
                <Flex column gap="gap.small">
                    <DragDropText>
                        Glissez-déposez un document ici ou
                    </DragDropText>
                    <DragDropTextLink>
                        cliquez pour choisir un document
                    </DragDropTextLink>
                </Flex>
            </div>
        </div>
    );
};

export default DropzoneGlobal;

const baseStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    borderWidth: 2,
    borderRadius: 8,
    borderColor: "#9299F7",
    borderStyle: "dashed",
    backgroundColor: "#E8EBFA",
    color: "#bdbdbd",
    outline: "none",
    height: "200px",
    width: "100%",
    gap: 15,
    cursor: "pointer",
    transition: "border .24s ease-in-out",
};

const focusedStyle = {
    borderColor: "#2196f3",
};

const acceptStyle = {
    borderColor: "#00e676",
};

const rejectStyle = {
    borderColor: "#ff1744",
};

const Informations = styled.div`
    display: ${({ showInformationFile }) =>
        showInformationFile ? "flex" : "none"};
    flex-direction: column;
    margin-top: 8px;
    .MuiTypography-caption {
        font-style: normal;
        font-weight: 400;
        font-size: 12px;
        line-height: 166%;
    }
`;

const DragDropTextLink = styled.span`
    color: #5b5fc7;
    text-align: center;

    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
    text-decoration-line: underline;
`;

const DragDropText = styled.p`
    color: #242424;
    text-align: center;

    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
`;

export const CloseIcon = styled.div`
    position: absolute;
    right: -10px;
    top: -8px;
    z-index: 5;
`;

export const PreviewFile = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 8px;
    width: 90px;
    border-radius: 5px;
    padding: 15px 8px;
    background-color: white;
    box-shadow: 2px 2px 0px 0px #eeeeee;
    cursor: pointer;
    opacity: 0.8;
    transition: all 0.3s;
    &:hover {
        opacity: 1;
    }
    span {
        color: black;
        font-size: 12px;
        width: 100%;
        word-wrap: break-word;
        text-align: center;
    }
`;

const WrapperPreview = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-top: 16px;
`;
